import * as React from "react";
import { Card, CardTitle, CardBody } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { axiosDownload } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import {
  useQueryGetAetnaComparePlans,
  useQueryGetAetnaCompareModelPlans,
} from "../../data/services/clientAdminService";
import { useQueryGetClientAetnaMappings } from "../../data/services/clientAetnaPlanMappingService";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../constants";
import AlertDialog from "../../components/AlertDialog";
import {
  formatPlanDisplayName,
  withClientIdFileName,
} from "./menuCompareUtils";

export const AETNA_COMPARE_FILE_NAME =
  "PrestigePEO Aetna to Multi Carrier Comparison.xlsx";

// Deliberately does not contain AETNA_COMPARE_FILE_NAME as a substring, so a lookup
// for either button - in tests or by a user reading the page - resolves to one card.
export const MODEL_AETNA_COMPARE_FILE_NAME =
  "PrestigePEO Model Based Aetna to Multi Carrier Comparison.xlsx";

// The third card's download - the browser-side name deliberately carries no year,
// exactly as the two above differ from the server's own stream name.
export const MAPPED_AETNA_COMPARE_FILE_NAME =
  "PrestigePEO Mapped Aetna to Multi Carrier Comparison.xlsx";

/**
 * One comparison card: the blue box, its dialog with the two multi-selects, and the
 * download. Every card holds its own dialog and selection state, so opening one does
 * not disturb the other.
 *
 * Which plans each dropdown offers is the caller's decision - the two cards differ
 * only in where their lists come from. Both download through the same endpoint, which
 * is driven purely by plan ids.
 *
 * showDownloadAll adds a "Download All" button beside "Download Selected", covering
 * every plan in both dropdowns without the user ticking each one. Opt-in per card
 * rather than always on, since only the assignment-sourced card asked for it.
 */
const AetnaCompareCard = ({
  oePeriodId,
  clientId,
  cardTitle,
  dialogTitle,
  fileName,
  description,
  dialogIntro,
  plansLoading,
  aetnaPlanOptions,
  nonAetnaPlanOptions,
  emptyMessage,
  showDownloadAll,
  testId,
}) => {
  const [dialogVisible, setDialogVisible] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [selectedAetnaPlans, setSelectedAetnaPlans] = React.useState([]);
  const [selectedPlans, setSelectedPlans] = React.useState([]);
  // Separate open state per multi-select, so opening one does not pop the other.
  const [aetnaOpened, setAetnaOpened] = React.useState(false);
  const [opened, setOpened] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState(null);

  const resetSelections = () => {
    setDialogVisible(false);
    setSelectedAetnaPlans([]);
    setSelectedPlans([]);
    setAetnaOpened(false);
    setOpened(false);
    setAlertMessage(null);
  };

  const runDownload = async (aetnaPlans, comparePlans) => {
    setIsDownloading(true);

    try {
      await axiosDownload(
        SERVICE_URLS.CLIENT_AETNA_COMPARE_DOWNLOAD(
          oePeriodId,
          clientId,
          aetnaPlans.map((plan) => plan.planId),
          comparePlans.map((plan) => plan.planId)
        ),
        withClientIdFileName(fileName, clientId)
      );
      resetSelections();
    } catch (error) {
      setAlertMessage(
        "The comparison could not be generated. Please try again."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadFile = async () => {
    if (selectedAetnaPlans.length === 0) {
      setAlertMessage(
        "Please select at least one Aetna plan to compare against."
      );
      return;
    }

    if (selectedPlans.length === 0) {
      setAlertMessage("Please select at least one plan to compare.");
      return;
    }

    await runDownload(selectedAetnaPlans, selectedPlans);
  };

  // Every plan in both dropdowns, so a user who wants the whole picture does not
  // have to tick each one. Reads the option lists rather than the selections, so
  // whatever the user has already picked is ignored rather than added to.
  const downloadAllPlans = async () => {
    if (aetnaPlanOptions.length === 0 || nonAetnaPlanOptions.length === 0) {
      setAlertMessage(
        "There are no plans available to compare for this client."
      );
      return;
    }

    await runDownload(aetnaPlanOptions, nonAetnaPlanOptions);
  };

  return (
    <Card style={{ width: 300, margin: "10px" }} type="info" data-testid={testId}>
      {dialogVisible && (
        <Dialog title={dialogTitle} onClose={resetSelections} width={"60vw"}>
          <div>
            <p>{dialogIntro}</p>

            {emptyMessage && <p>{emptyMessage}</p>}

            <MultiSelect
              data={aetnaPlanOptions}
              onChange={(event) => setSelectedAetnaPlans(event.target.value)}
              onBlur={() => setAetnaOpened(false)}
              onOpen={() => setAetnaOpened(true)}
              opened={aetnaOpened}
              loading={plansLoading}
              dataItemKey="planId"
              textField="planName"
              value={selectedAetnaPlans}
              label="Select Aetna plans to compare against."
              placeholder="Please select ..."
              style={{ width: "100%" }}
            />

            <MultiSelect
              data={nonAetnaPlanOptions}
              onChange={(event) => setSelectedPlans(event.target.value)}
              onBlur={() => setOpened(false)}
              onOpen={() => setOpened(true)}
              opened={opened}
              loading={plansLoading}
              dataItemKey="planId"
              textField="planName"
              value={selectedPlans}
              label="Select plans to compare."
              placeholder="Please select ..."
              style={{ width: "100%" }}
            />
          </div>

          {alertMessage && (
            <AlertDialog
              title={"Alert!"}
              message={alertMessage}
              onClose={() => setAlertMessage(null)}
            />
          )}

          <DialogActionsBar layout={"end"}>
            <div style={{ paddingRight: "8px", paddingBottom: "8px" }}>
              {showDownloadAll && (
                <Button
                  themeColor={"primary"}
                  onClick={downloadAllPlans}
                  disabled={isDownloading}
                  style={{ marginRight: "10px" }}
                >
                  {isDownloading ? (
                    <span className="k-icon k-i-loading"></span>
                  ) : null}{" "}
                  {"Download All"}
                </Button>
              )}
              <Button
                themeColor={"primary"}
                onClick={downloadFile}
                disabled={isDownloading}
                style={{ marginRight: "10px" }}
              >
                {isDownloading ? (
                  <span className="k-icon k-i-loading"></span>
                ) : null}{" "}
                {"Download Selected"}
              </Button>
              <Button onClick={resetSelections} style={{ marginRight: "10px" }}>
                {"Close"}
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      <CardBody>
        <CardTitle>
          <b>{cardTitle}</b>
        </CardTitle>
        <p>{description}</p>
        <p>
          <Button
            themeColor={"primary"}
            className={"oe-multiline-button k-button-flat k-button-flat-primary"}
            onClick={() => setDialogVisible(true)}
            disabled={isDownloading}
          >
            {fileName}{" "}
            {isDownloading ? (
              <span className="k-icon k-i-loading"></span>
            ) : (
              ""
            )}
          </Button>
        </p>
      </CardBody>
    </Card>
  );
};

/**
 * The third card: no dialog, because there is nothing to select. An administrator
 * has already stored, per client per season, which Aetna plan maps to which up-to-
 * three alternatives, and the export turns each stored mapping into its own tab.
 * All this card can do is state how many mappings it will export and download them.
 *
 * Rendered only when the mapping query returned rows - a card that can do nothing
 * should not be there - so mappingCount is always at least 1 by the time this mounts.
 */
const MappedAetnaCompareCard = ({ oePeriodId, clientId, mappingCount }) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState(null);

  const downloadFile = async () => {
    setIsDownloading(true);

    try {
      await axiosDownload(
        SERVICE_URLS.CLIENT_AETNA_COMPARE_MAPPED_DOWNLOAD(oePeriodId, clientId),
        withClientIdFileName(MAPPED_AETNA_COMPARE_FILE_NAME, clientId)
      );
    } catch (error) {
      setAlertMessage(
        "The comparison could not be generated. Please try again."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card
      style={{ width: 300, margin: "10px" }}
      type="info"
      data-testid="mapped-aetna-compare-card"
    >
      <CardBody>
        <CardTitle>
          <b>Mapped Aetna Renewal to Multi Carrier Comparison</b>
        </CardTitle>
        <p>
          Compare each Aetna plan against the alternatives already mapped to
          it for this client and season, at the selected season's renewal
          rates.
        </p>
        <p>
          {mappingCount} mapped plan{mappingCount === 1 ? "" : "s"}
        </p>

        {alertMessage && (
          <AlertDialog
            title={"Alert!"}
            message={alertMessage}
            onClose={() => setAlertMessage(null)}
          />
        )}

        <p>
          <Button
            themeColor={"primary"}
            className={"oe-multiline-button k-button-flat k-button-flat-primary"}
            onClick={downloadFile}
            disabled={isDownloading}
          >
            {"Download"}{" "}
            {isDownloading ? (
              <span className="k-icon k-i-loading"></span>
            ) : (
              ""
            )}
          </Button>
        </p>
      </CardBody>
    </Card>
  );
};

/**
 * "Aetna Renewal to Multi Carrier Plan Comparison" section, rendered at the bottom of
 * the Rate Menu Comparison page for clients on the "aetna" allow-list.
 *
 * Three cards side by side, differing only in where their plans come from - the
 * client's assignments, the season's rate models, or the mapping table:
 *
 *  - The first reads the client's plan assignments. Not period-scoped: a plan
 *    qualifies on being an active assignment in the portal.
 *  - The second reads every rate model built for the SELECTED period, so it also
 *    offers a plan a user has added to a model but that the client does not yet hold,
 *    which is the lineup actually being priced during renewal.
 *  - The third has no selection at all: an administrator has already stored the
 *    Aetna-to-alternatives mappings for this client and season, so the card just
 *    states how many there are and downloads them, one tab per mapping. Hidden
 *    entirely when the client has none.
 *
 * The first two share one dialog shape with two multi-selects - the Aetna plans and
 * the non-Aetna plans to compare them against - and the API decides the Aetna/
 * non-Aetna split by carrier, so the two lists on a card can never overlap or drift.
 * Every export prices every column at the selected season's renewal rates, with one
 * tab per Aetna plan (selected, or mapped) holding that plan in the leftmost column,
 * where it is also the baseline the rest of the tab is measured against, followed by
 * every compared plan. Excel only.
 *
 * Shared by the client and clientadmin copies of ClientMenuCompare so the section
 * exists once rather than in both.
 */
const AetnaMultiCarrierCompare = ({ clientId, oePeriodId }) => {
  const hasAetnaFeature = useFeatureAccess(
    FEATURE_KEYS.Aetna,
    oePeriodId,
    clientId
  );

  // All three gated on the feature flag, so a client without the key never issues
  // any of them - the endpoints would 404 anyway.
  const { isLoading: plansLoading, data: planLists } =
    useQueryGetAetnaComparePlans(oePeriodId, clientId, hasAetnaFeature);

  const { isLoading: modelPlansLoading, data: modelPlanLists } =
    useQueryGetAetnaCompareModelPlans(oePeriodId, clientId, hasAetnaFeature);

  const { data: mappingLists } = useQueryGetClientAetnaMappings(
    oePeriodId,
    clientId,
    hasAetnaFeature
  );

  const mappingCount = mappingLists?.data?.length ?? 0;

  const aetnaPlanOptions = React.useMemo(() => {
    return (planLists?.data?.aetnaPlans || []).map(formatPlanDisplayName);
  }, [planLists]);

  const nonAetnaPlanOptions = React.useMemo(() => {
    return (planLists?.data?.comparePlans || []).map(formatPlanDisplayName);
  }, [planLists]);

  const modelAetnaPlanOptions = React.useMemo(() => {
    return (modelPlanLists?.data?.aetnaPlans || []).map(formatPlanDisplayName);
  }, [modelPlanLists]);

  const modelNonAetnaPlanOptions = React.useMemo(() => {
    return (modelPlanLists?.data?.comparePlans || []).map(formatPlanDisplayName);
  }, [modelPlanLists]);

  // Named rather than left as two silent empty dropdowns. Models holding no Aetna
  // plan at all is the expected state for a client whose models were built entirely
  // on the incoming carrier, so it needs to read as an answer, not a failure.
  const modelEmptyMessage = React.useMemo(() => {
    if (modelPlansLoading) {
      return null;
    }

    if (
      modelAetnaPlanOptions.length === 0 &&
      modelNonAetnaPlanOptions.length === 0
    ) {
      return "No rate models exist for this open enrollment season, so there are no plans to compare.";
    }

    if (modelAetnaPlanOptions.length === 0) {
      return "No Aetna plans were found in this season's rate models.";
    }

    return null;
  }, [modelPlansLoading, modelAetnaPlanOptions, modelNonAetnaPlanOptions]);

  if (!hasAetnaFeature) {
    return null;
  }

  return (
    <div>
      <div style={{ paddingBottom: 10 }}>
        <h2 style={{ margin: "10px", marginTop: "40px" }}>
          Aetna Renewal to Multi Carrier Plan Comparison
        </h2>
      </div>
      <div style={{ display: "flex" }}>
        <AetnaCompareCard
          oePeriodId={oePeriodId}
          clientId={clientId}
          testId="aetna-compare-card"
          cardTitle="Aetna Renewal to Multi Carrier Comparison"
          dialogTitle="Aetna Renewal to Multi Carrier Plan Comparison"
          fileName={AETNA_COMPARE_FILE_NAME}
          description="Compare Aetna plans against other carriers at the selected season's renewal rates"
          dialogIntro="Renewal rates for the selected open enrollment season are shown for every plan. The workbook has one tab per Aetna plan you select, each showing that plan in the first column with the compared plans to the right of it."
          plansLoading={plansLoading}
          aetnaPlanOptions={aetnaPlanOptions}
          nonAetnaPlanOptions={nonAetnaPlanOptions}
          showDownloadAll
        />

        <AetnaCompareCard
          oePeriodId={oePeriodId}
          clientId={clientId}
          testId="model-aetna-compare-card"
          cardTitle="Model Based Aetna Renewal to Multi Carrier Comparison"
          dialogTitle="Model Based Aetna Renewal to Multi Carrier Comparison"
          fileName={MODEL_AETNA_COMPARE_FILE_NAME}
          description="Compare Aetna plans from this season's rate models against other carriers at the selected season's renewal rates"
          dialogIntro="Both lists hold the plans found in this season's rate models, including plans not yet assigned to the client. Renewal rates for the selected open enrollment season are shown for every plan. The workbook has one tab per Aetna plan you select, each showing that plan in the first column with the compared plans to the right of it."
          plansLoading={modelPlansLoading}
          aetnaPlanOptions={modelAetnaPlanOptions}
          nonAetnaPlanOptions={modelNonAetnaPlanOptions}
          emptyMessage={modelEmptyMessage}
        />

        {mappingCount > 0 && (
          <MappedAetnaCompareCard
            oePeriodId={oePeriodId}
            clientId={clientId}
            mappingCount={mappingCount}
          />
        )}
      </div>
    </div>
  );
};

export default AetnaMultiCarrierCompare;
